/**
 * AppState - Zustand store for Lenders App authentication state
 * Based on Members App pattern, simplified for Lenders (no SMS, no registration flow)
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import ProfileApi from "../api/ProfileApi";
import { ProfileApiResponse } from "../api/Types";
import { supabase } from "../lib/supabaseClient";

// Organization user data from org_users table
export interface OrgUserData {
  org_id: string;
  org_name?: string;
  role: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface AppState {
  isAuthenticated: "yes" | "no" | "maybe";
  profile: ProfileApiResponse | undefined | null;
  orgUser: OrgUserData | undefined | null;
  isLoggedOut: boolean;
  isFetchingProfile: boolean;
  lastSessionCheck: number; // Timestamp of last session verification
  resetState: () => void;
  clearProfileData: () => void;
  fetchProfileData: (forceCheck?: boolean) => Promise<boolean>;
  setProfileData: (newData: ProfileApiResponse) => void;
  clearLogoutFlag: () => void;
}

// 🔒 SECURITY: Development-only authentication bypass
const isDevelopmentMode = import.meta.env.DEV;
const enableDevBypass =
  isDevelopmentMode &&
  (import.meta.env.VITE_DEV_AUTH_BYPASS === true ||
    import.meta.env.VITE_DEV_AUTH_BYPASS === "true");

if (enableDevBypass) {
  console.log("🔧 DEV MODE: Authentication bypass enabled");
}

// 🔒 SECURITY: Mock profile data for development only
const createMockProfile = (): ProfileApiResponse => ({
  type: "Lender",
  user_id: "dev-lender-123",
  name: "Dev Lender",
  email: "dev-lender@example.com",
  email_verified: true,
  created_at: new Date().toISOString(),
  latest_login: new Date().toISOString(),
  logins_count: 1,
  user_metadata: {
    firstName: "Dev",
    lastName: "Lender",
    initials: "DL",
    organization: "Dev Credit Union",
    role: "Loan Officer",
  },
  app_metadata: {
    registration: {
      is_registered: true,
      email_verified: true,
      date_registration_completed: new Date().toISOString(),
      agreed_to_terms: true,
    },
  },
  registered: true,
  registered_submitted: new Date().toISOString(),
});

const useAppState = create<AppState>()(
  persist(
    (set, get) => ({
      isAuthenticated: "maybe",
      profile: undefined,
      orgUser: undefined,
      isLoggedOut: false,
      isFetchingProfile: false,
      lastSessionCheck: 0,

      resetState: () => {
        set(() => ({
          isAuthenticated: "maybe",
          profile: undefined,
          orgUser: undefined,
          isLoggedOut: false,
          isFetchingProfile: false,
          lastSessionCheck: 0,
        }));
      },

      clearProfileData: () => {
        set(() => ({
          isAuthenticated: "no",
          profile: null,
          orgUser: null,
          isLoggedOut: true,
          isFetchingProfile: false,
          lastSessionCheck: 0,
        }));
      },

      clearLogoutFlag: () => {
        set(() => ({
          isLoggedOut: false,
        }));
      },

      fetchProfileData: async (forceCheck = false) => {
        console.log("🔍 fetchProfileData: Called", { forceCheck });

        const state = get();
        if (state.isFetchingProfile) {
          console.log("⏭️ fetchProfileData: Already fetching, skipping");
          return false;
        }

        // Check if we need to verify the session
        // Session check is required if:
        // 1. forceCheck is true (explicit request)
        // 2. No session check has been done yet (lastSessionCheck === 0)
        // 3. Last session check was more than 5 minutes ago (stale check)
        const now = Date.now();
        const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
        const needsSessionCheck =
          forceCheck ||
          state.lastSessionCheck === 0 ||
          (now - state.lastSessionCheck) > SESSION_CHECK_INTERVAL;

        if (state.isAuthenticated === "yes" && state.profile && !needsSessionCheck) {
          console.log("✅ fetchProfileData: Already authenticated and session recently verified, skipping");
          return true;
        }

        if (state.isLoggedOut) {
          console.log("🚫 fetchProfileData: User logged out, not auto-logging in");
          return false;
        }

        console.log("🔍 fetchProfileData: Verifying session with backend...");
        set(() => ({ isFetchingProfile: true }));

        // 🔒 SECURITY: Development bypass - ONLY active when explicitly enabled
        if (enableDevBypass) {
          console.log("🔧 DEV MODE: Using mock authentication");
          const mockProfile = createMockProfile();
          set(() => ({
            profile: mockProfile,
            orgUser: {
              org_id: 'dev-org-123',
              org_name: 'Dev Credit Union',
              role: 'admin',
              first_name: 'Dev',
              last_name: 'Lender',
              email: 'dev-lender@example.com',
            },
            isAuthenticated: "yes",
            isFetchingProfile: false,
            lastSessionCheck: Date.now(),
          }));
          return true;
        }

        // 🔒 SECURITY: Production authentication flow
        console.log("🔍 fetchProfileData: Calling /verascore/profile");
        await ProfileApi(
          async (data) => {
            console.log("✅ fetchProfileData: Auth0 profile loaded");

            // Fetch org_users data for this lender
            let orgUserData: OrgUserData | null = null;
            try {
              console.log("🏢 fetchProfileData: Fetching org_users for", data.user_id);
              const { data: orgUser, error: orgUserError } = await supabase
                .from('org_users')
                .select('org_id, role, first_name, last_name, email')
                .eq('auth0_user_id', data.user_id)
                .single();

              if (orgUserError) {
                console.warn("⚠️ fetchProfileData: org_users lookup failed", orgUserError);
              } else if (orgUser) {
                console.log("✅ fetchProfileData: org_users found", orgUser);

                // Fetch organization name separately
                let orgName = 'Unknown Organization';
                const { data: org, error: orgError } = await supabase
                  .from('organizations')
                  .select('legal_name')
                  .eq('org_id', orgUser.org_id)
                  .single();

                if (!orgError && org) {
                  orgName = org.legal_name;
                  console.log("✅ fetchProfileData: organization found", orgName);
                } else {
                  console.warn("⚠️ fetchProfileData: organization lookup failed", orgError);
                }

                orgUserData = {
                  org_id: orgUser.org_id,
                  org_name: orgName,
                  role: orgUser.role,
                  first_name: orgUser.first_name,
                  last_name: orgUser.last_name,
                  email: orgUser.email,
                };
              }
            } catch (err) {
              console.error("❌ fetchProfileData: org_users error", err);
            }

            set(() => ({
              profile: data,
              orgUser: orgUserData,
              isAuthenticated: "yes",
              isFetchingProfile: false,
              lastSessionCheck: Date.now(), // Record successful session verification
            }));
          },
          (data) => {
            console.log("❌ fetchProfileData: Failed", data);
            set(() => ({
              profile: null,
              orgUser: null,
              isAuthenticated: "no",
              isFetchingProfile: false,
            }));
          },
          (data) => {
            console.log("❌ fetchProfileData: Rejected", data);
            set(() => ({
              profile: null,
              orgUser: null,
              isAuthenticated: "no",
              isFetchingProfile: false,
            }));
          }
        );
        return true;
      },

      setProfileData: async (newData: ProfileApiResponse) => {
        console.log("🔍 setProfileData: Setting profile and fetching org user");

        // First set the profile data
        set(() => ({
          profile: newData,
          isAuthenticated: "yes",
          isFetchingProfile: true,
        }));

        // Then fetch org_users data for this lender
        let orgUserData: OrgUserData | null = null;
        try {
          console.log("🏢 setProfileData: Fetching org_users for", newData.user_id);
          const { data: orgUser, error: orgUserError } = await supabase
            .from('org_users')
            .select('org_id, role, first_name, last_name, email')
            .eq('auth0_user_id', newData.user_id)
            .single();

          if (orgUserError) {
            console.warn("⚠️ setProfileData: org_users lookup failed", orgUserError);
          } else if (orgUser) {
            console.log("✅ setProfileData: org_users found", orgUser);

            // Fetch organization name separately
            let orgName = 'Unknown Organization';
            const { data: org, error: orgError } = await supabase
              .from('organizations')
              .select('legal_name')
              .eq('org_id', orgUser.org_id)
              .single();

            if (!orgError && org) {
              orgName = org.legal_name;
              console.log("✅ setProfileData: organization found", orgName);
            } else {
              console.warn("⚠️ setProfileData: organization lookup failed", orgError);
            }

            orgUserData = {
              org_id: orgUser.org_id,
              org_name: orgName,
              role: orgUser.role,
              first_name: orgUser.first_name,
              last_name: orgUser.last_name,
              email: orgUser.email,
            };
          }
        } catch (err) {
          console.error("❌ setProfileData: org_users error", err);
        }

        set(() => ({
          orgUser: orgUserData,
          isFetchingProfile: false,
        }));
      },
    }),
    {
      name: "verascore-lender-auth-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        profile: state.profile,
        orgUser: state.orgUser,
        isLoggedOut: state.isLoggedOut,
        lastSessionCheck: state.lastSessionCheck,
      }),
    }
  )
);

export default useAppState;

